import type { Shift, ShiftAssignment } from '@/types/shift.types';
import { addDays, format, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getAssignmentShift, getAssignmentUserId } from '../utils/assignmentUtils';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Avatar } from '@/components/common/Avatar';
import { Badge } from '@/components/ui';
import { EmptyCell } from './EmptyCell';
import { ShiftBlock } from './ShiftBlock';
import { usePermissions } from '@/hooks/usePermissions';
import type { ShiftViewMode as ViewMode } from '../utils/dateRange';
import { cn } from '@/lib/cn';
import { getCompanyColor } from '../utils/companyColor';
import { useFitToWindow } from '../hooks/useFitToWindow';
import { ScheduleScrollTrack } from './ScheduleScrollTrack';
import { useMediaQuery } from '@/hooks/useMediaQuery';

function toDateKey(dateStr: string): string {
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    return format(parseISO(dateStr), 'yyyy-MM-dd');
  } catch {
    return dateStr;
  }
}

function getDaysInRange(startDate: string, endDate: string): string[] {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const days: string[] = [];
  let d = start;
  while (d <= end) {
    days.push(format(d, 'yyyy-MM-dd'));
    d = addDays(d, 1);
  }
  return days;
}

const HH_MM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

interface ShiftCellSegment {
  assignment: ShiftAssignment;
  continuesFromPreviousDay?: boolean;
}

function nextDateKey(dateKey: string): string {
  return format(addDays(parseISO(dateKey), 1), 'yyyy-MM-dd');
}

function minutesFromTime(time?: string): number | null {
  if (!time) return null;
  const match = HH_MM_RE.exec(time);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function crossesMidnight(startTime?: string, endTime?: string): boolean {
  const start = minutesFromTime(startTime);
  const end = minutesFromTime(endTime);
  return start !== null && end !== null && end < start;
}

function pushSegment(
  map: Map<string, ShiftCellSegment[]>,
  key: string,
  segment: ShiftCellSegment
): void {
  const arr = map.get(key);
  if (arr) arr.push(segment);
  else map.set(key, [segment]);
}

function addAssignmentSegments(
  map: Map<string, ShiftCellSegment[]>,
  assignment: ShiftAssignment,
  shiftById: Map<string, Shift>,
  buildKey: (dateKey: string) => string
): void {
  const dateKey = toDateKey(assignment.date);
  pushSegment(map, buildKey(dateKey), { assignment });

  const shift = getAssignmentShift(assignment, shiftById);
  if (crossesMidnight(shift?.startTime, shift?.endTime)) {
    pushSegment(map, buildKey(nextDateKey(dateKey)), {
      assignment,
      continuesFromPreviousDay: true,
    });
  }
}

function PastDateCell({
  variant = 'default',
}: {
  variant?: 'default' | 'monthly';
}): React.ReactElement {
  return (
    <div
      className={cn(
        'w-full rounded-md border border-transparent',
        variant === 'monthly' ? 'min-h-[40px]' : 'min-h-[52px]'
      )}
      aria-label="Past date"
    />
  );
}

export interface PlanningGridUser {
  _id: string;
  name: string;
  /** Optional company scope; when set with `companyGroups`, the grid groups rows by company. */
  companyId?: string;
  companyName?: string;
  companyTags?: PlanningGridCompanyTag[];
  roleId?: string;
  roleName?: string;
  roleSlug?: string;
  roleNames?: string[];
}

export interface PlanningGridCompanyTag {
  companyId: string;
  companyName: string;
}

export interface PlanningGridCompanyGroup {
  companyId: string;
  companyName: string;
}

export interface ShiftPlanningGridProps {
  viewMode: ViewMode;
  startDate: string;
  endDate: string;
  users: PlanningGridUser[];
  assignments: ShiftAssignment[];
  shifts: Shift[];
  onAssign: (
    userId: string,
    date: string,
    companyId?: string,
    companyOptions?: PlanningGridCompanyTag[]
  ) => void;
  onEdit: (assignment: ShiftAssignment) => void;
  onCancel?: (assignment: ShiftAssignment) => void;
  myAssignmentsOnly: boolean;
  /** Optional ordered list of company groups to render as sub-headers (only takes effect when users have companyId). */
  companyGroups?: PlanningGridCompanyGroup[];
  /** Assignment to visually highlight (e.g. when arriving from a shift notification). */
  highlightAssignmentId?: string;
}

function getRoleBadgeClasses(roleName?: string, roleSlug?: string): string {
  const token = `${roleSlug ?? ''} ${roleName ?? ''}`.toLowerCase();
  if (token.includes('manager') || token.includes('management')) {
    return 'border-sky-200/80 bg-sky-50 text-sky-700 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200';
  }
  if (token.includes('hr') || token.includes('human resource')) {
    return 'border-rose-200/80 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200';
  }
  if (token.includes('supervisor') || token.includes('lead')) {
    return 'border-amber-200/80 bg-amber-50 text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200';
  }
  if (token.includes('staff') || token.includes('member')) {
    return 'border-emerald-200/80 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200';
  }
  return 'border-violet-200/80 bg-violet-50 text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-200';
}

function RolePills({
  roleNames,
  roleName,
  roleSlug,
}: {
  roleNames?: string[];
  roleName?: string;
  roleSlug?: string;
}): React.ReactElement | null {
  const labels = roleNames?.length ? roleNames : roleName ? [roleName] : [];
  if (labels.length === 0) return null;
  const visible = labels.slice(0, 2);
  const extraCount = labels.length - visible.length;

  return (
    <>
      {visible.map((label, index) => (
        <Badge
          key={`${label}:${index}`}
          className={cn(
            'min-h-0 px-1.5 py-px text-[10px] font-semibold',
            getRoleBadgeClasses(label, index === 0 ? roleSlug : undefined)
          )}
        >
          {label}
        </Badge>
      ))}
      {extraCount > 0 ? (
        <Badge size="sm" variant="neutral">
          +{extraCount}
        </Badge>
      ) : null}
    </>
  );
}

function CompanyPills({
  companyTags,
  companyId,
  companyName,
}: {
  companyTags?: PlanningGridCompanyTag[];
  companyId?: string;
  companyName?: string;
}): React.ReactElement | null {
  const tags = companyTags?.length
    ? companyTags
    : companyId && companyName
      ? [{ companyId, companyName }]
      : [];
  if (tags.length === 0) return null;

  const visible = tags.slice(0, 2);
  const extraCount = tags.length - visible.length;
  return (
    <>
      {visible.map((tag) => {
        const companyColor = getCompanyColor(tag.companyId);
        return (
          <span
            key={tag.companyId}
            title={tag.companyName}
            className={cn(
              'inline-block max-w-full truncate rounded-full px-1.5 py-px text-[10px] font-medium',
              companyColor.bg,
              companyColor.text
            )}
          >
            {tag.companyName}
          </span>
        );
      })}
      {extraCount > 0 ? (
        <CompanyOverflowPill tags={tags.slice(visible.length)} />
      ) : null}
    </>
  );
}

function CompanyOverflowPill({ tags }: { tags: PlanningGridCompanyTag[] }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const close = useCallback(() => setOpen(false), []);

  const toggle = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({ top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 232) });
    }
    setOpen((current) => !current);
  };

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) close();
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    const handleViewportChange = (): void => close();
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [close, open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Show ${tags.length} more companies`}
        className="inline-flex min-h-5 items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-px text-[10px] font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
      >
        +{tags.length}
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Other companies"
            className="fixed z-[9999] w-56 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
            style={{ top: position.top, left: Math.max(8, position.left) }}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-700">
              <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Other companies</span>
              <button
                type="button"
                onClick={close}
                aria-label="Close company list"
                className="rounded-md p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:hover:bg-gray-700 dark:hover:text-gray-100"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
            <ul className="max-h-56 overflow-y-auto p-1.5">
              {tags.map((tag) => {
                const companyColor = getCompanyColor(tag.companyId);
                return (
                  <li key={tag.companyId} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-gray-700 dark:text-gray-200">
                    <span className={cn('h-2 w-2 shrink-0 rounded-full', companyColor.dotBg)} aria-hidden />
                    <span className="min-w-0 truncate">{tag.companyName}</span>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Memoised: the page re-renders on every menu toggle, filter or modal, and
 * redrawing every cell of a month for that made clicks feel slow. The page
 * keeps the props stable so the grid only redraws when its data changes.
 */
function isWeekend(dateKey: string): boolean {
  const day = parseISO(dateKey).getDay();
  return day === 0 || day === 6;
}

export const ShiftPlanningGrid = memo(function ShiftPlanningGrid({
  viewMode,
  startDate,
  endDate,
  users,
  assignments,
  shifts,
  onAssign,
  onEdit,
  myAssignmentsOnly,
  companyGroups,
  highlightAssignmentId,
}: ShiftPlanningGridProps): React.ReactElement {
  const days = useMemo(() => getDaysInRange(startDate, endDate), [startDate, endDate]);
  // Checked once here rather than by a PermissionGate in each of the ~1,000 cells.
  const { can } = usePermissions();
  const canCreate = can('shifts', 'create');
  const isMonthly = viewMode === 'monthly';

  const shiftById = useMemo(() => {
    const map = new Map<string, Shift>();
    shifts.forEach((s) => map.set(s._id, s));
    return map;
  }, [shifts]);

  const assignmentsByCompanyUserDate = useMemo(() => {
    const map = new Map<string, ShiftCellSegment[]>();
    assignments.forEach((a) => {
      const uid = getAssignmentUserId(a);
      const cid = (a as { companyId?: string }).companyId ?? '__none__';
      addAssignmentSegments(map, a, shiftById, (dateKey) => `${cid}:${uid}:${dateKey}`);
    });
    return map;
  }, [assignments, shiftById]);

  /** Flat-mode lookup: all assignments for a (userId, date) regardless of company. */
  const assignmentsByUserDate = useMemo(() => {
    const map = new Map<string, ShiftCellSegment[]>();
    assignments.forEach((a) => {
      const uid = getAssignmentUserId(a);
      addAssignmentSegments(map, a, shiftById, (dateKey) => `${uid}:${dateKey}`);
    });
    return map;
  }, [assignments, shiftById]);

  const companyNameByIdMap = useMemo(() => {
    const m = new Map<string, string>();
    companyGroups?.forEach((g) => m.set(g.companyId, g.companyName));
    users.forEach((u) => {
      if (u.companyId && u.companyName && !m.has(u.companyId)) m.set(u.companyId, u.companyName);
    });
    return m;
  }, [companyGroups, users]);

  const grouped = useMemo(() => {
    if (!companyGroups || companyGroups.length === 0 || !users.some((u) => u.companyId)) {
      return null;
    }
    const byCompany = new Map<string, PlanningGridUser[]>();
    users.forEach((u) => {
      const cid = u.companyId ?? '__none__';
      if (!byCompany.has(cid)) byCompany.set(cid, []);
      byCompany.get(cid)!.push(u);
    });
    return companyGroups
      .map((g) => ({ ...g, members: byCompany.get(g.companyId) ?? [] }))
      .filter((g) => g.members.length > 0);
  }, [companyGroups, users]);

  /** Company headers and member rows as one list, so only the visible ones are drawn. */
  const rows = useMemo<GridRow[]>(() => {
    if (!grouped) return users.map((user) => ({ kind: 'user', key: user._id, user }));
    return grouped.flatMap((g): GridRow[] => [
      { kind: 'group', key: `group:${g.companyId}`, companyId: g.companyId, companyName: g.companyName },
      ...g.members.map((user): GridRow => ({ kind: 'user', key: `${g.companyId}:${user._id}`, user })),
    ]);
  }, [grouped, users]);

  const todayDateKey = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

  const tableLabel = `Shift assignments ${format(parseISO(startDate), 'd MMM')} – ${format(parseISO(endDate), 'd MMM yyyy')}`;

  // Fixed-width columns so weekly and monthly cards look identical (no stretching).
  const STICKY_W = 230;
  const COL_W = isMonthly ? 130 : viewMode === 'daily' ? 320 : 150;

  // Scroll the grid so today (or, if today is out of range, the right edge) is visible.
  // Must be declared before any conditional return to keep hook order stable when
  // `users` flips between empty/non-empty (e.g. while the user types in the search box).
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const hasUsers = users.length > 0;
  // One scroller, sized to end at the bottom of the window (desktop). The wheel
  // is left alone: vertical scrolls rows, Shift+wheel or a trackpad moves across
  // days. Turning the vertical wheel sideways trapped Windows mouse users, whose
  // wheel only scrolls vertically, in the grid instead of the page.
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const fitHeight = useFitToWindow(scrollerRef, isDesktop && hasUsers);

  // Only the rows in view (plus a few either side) are built. A month for 80+
  // people across 50+ companies is thousands of cells, and building them all
  // made opening, leaving and re-grouping the page slow. Needs the grid's own
  // fixed height, so phones (where the page scrolls) still get every row. On
  // by the first render: waiting for the measured height built every row once.
  const virtualize = isDesktop && hasUsers;
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: (index) => (rows[index]?.kind === 'group' ? 30 : isMonthly ? 58 : 62),
    getItemKey: (index) => rows[index]?.key ?? index,
    overscan: 6,
    // Until the scroller exists, assume it is about a window tall.
    initialRect: { width: 0, height: typeof window === 'undefined' ? 800 : window.innerHeight },
    enabled: virtualize,
    useFlushSync: false,
  });

  // A deep-linked shift may sit in a row that isn't built yet: bring its row
  // into view first, then centre the shift itself once it renders.
  useEffect(() => {
    if (!highlightAssignmentId || !virtualize) return;
    const target = assignments.find((a) => a._id === highlightAssignmentId);
    if (!target) return;
    const uid = getAssignmentUserId(target);
    const cid = (target as { companyId?: string }).companyId;
    const index = rows.findIndex(
      (row) =>
        row.kind === 'user' &&
        row.user._id === uid &&
        (!row.user.companyId || row.user.companyId === cid)
    );
    if (index < 0) return;
    virtualizer.scrollToIndex(index, { align: 'center' });
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() =>
        document
          .getElementById(`shift-assignment-${highlightAssignmentId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      );
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [highlightAssignmentId, assignments, rows, virtualize, virtualizer]);

  const updateScrollControls = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  // The ‹ › buttons only change at the edges; checking once per frame is plenty.
  const controlsFrame = useRef(0);
  const onGridScroll = useCallback(() => {
    if (controlsFrame.current) return;
    controlsFrame.current = requestAnimationFrame(() => {
      controlsFrame.current = 0;
      updateScrollControls();
    });
  }, [updateScrollControls]);
  useEffect(() => () => cancelAnimationFrame(controlsFrame.current), []);

  useEffect(() => {
    const grid = scrollerRef.current;
    if (!grid || !hasUsers) return;
    const observer = new ResizeObserver(updateScrollControls);
    observer.observe(grid);
    const table = grid.querySelector('table');
    if (table) observer.observe(table);
    const raf = requestAnimationFrame(updateScrollControls);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [days, hasUsers, updateScrollControls]);

  useEffect(() => {
    if (!hasUsers) return;
    const el = scrollerRef.current;
    if (!el) return;
    const todayIdx = days.indexOf(todayDateKey);
    const apply = () => {
      if (viewMode === 'weekly') {
        el.scrollTo({ left: 0, behavior: 'auto' });
      } else if (todayIdx >= 0) {
        const target = STICKY_W + todayIdx * COL_W - (el.clientWidth - STICKY_W) / 2 + COL_W / 2;
        el.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
      } else {
        el.scrollTo({ left: el.scrollWidth, behavior: 'auto' });
      }
      updateScrollControls();
    };
    // Wait for the table to lay out (next frame) so scrollWidth is accurate.
    const raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [COL_W, days, todayDateKey, hasUsers, updateScrollControls, viewMode]);

  if (users.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400">
        {myAssignmentsOnly
          ? 'You have no shift assignments for this period.'
          : 'No team members to display. Add users or adjust filters.'}
      </div>
    );
  }

  const dateHeaderFormat = 'EEE d';
  const thDateClass =
    'px-2 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300';
  const tdCellPadding = 'px-2 py-1.5';
  const cellMinHeight = isMonthly ? 'min-h-[40px]' : 'min-h-[46px]';
  const dateColWidth =
    isMonthly ? 'w-[130px] min-w-[130px]' : viewMode === 'daily' ? 'w-[320px] min-w-[320px]' : 'w-[150px] min-w-[150px]';
  const stickyColClass =
    'sticky left-0 z-10 w-[230px] min-w-[230px] bg-white px-3 py-2 dark:bg-gray-800';
  const stickyThClass =
    'sticky left-0 z-20 w-[230px] min-w-[230px] bg-white px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  const rowHoverClass = 'border-b border-gray-100 dark:border-gray-800';

  const scrollByPage = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.max(COL_W, el.clientWidth - STICKY_W - COL_W),
      behavior: 'smooth',
    });
  };

  const virtualItems = virtualize ? virtualizer.getVirtualItems() : [];
  const visibleRows = virtualize
    ? virtualItems.map((item) => ({ row: rows[item.index], index: item.index }))
    : rows.map((row, index) => ({ row, index }));
  const paddingTop = virtualItems[0]?.start ?? 0;
  const paddingBottom = virtualItems.length
    ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
    : 0;
  const measureRef = virtualize ? virtualizer.measureElement : undefined;

  return (
    <div className="min-w-0 w-full">
      {canScrollLeft || canScrollRight ? (
        <div className="flex items-center gap-2 border-b border-gray-200 bg-white px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800">
          <button
            type="button"
            onClick={() => scrollByPage(-1)}
            disabled={!canScrollLeft}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-950 disabled:cursor-not-allowed disabled:opacity-35 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
            aria-label="Scroll schedule left"
            title="Scroll schedule left"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <ScheduleScrollTrack scrollerRef={scrollerRef} onPage={scrollByPage} />
          <button
            type="button"
            onClick={() => scrollByPage(1)}
            disabled={!canScrollRight}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-950 disabled:cursor-not-allowed disabled:opacity-35 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
            aria-label="Scroll schedule right"
            title="Scroll schedule right"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}
      <div
        ref={scrollerRef}
        className="schedule-grid-scroll min-w-0 w-full overflow-auto"
        style={
          fitHeight ? { maxHeight: fitHeight } : virtualize ? { maxHeight: '75vh' } : undefined
        }
        onScroll={onGridScroll}
      >
        <table className="w-max border-collapse text-sm" aria-label={tableLabel}>
          <thead className="sticky top-0 z-20 border-b border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-800">
            <tr>
              <th scope="col" className={stickyThClass}>
                Team member
              </th>
              {days.map((d) => {
                const isToday = d === todayDateKey;
                const weekend = isWeekend(d);
                return (
                  <th
                    key={d}
                    scope="col"
                    className={cn(
                      thDateClass,
                      dateColWidth,
                      isToday &&
                        'bg-primary/5 ring-inset ring-1 ring-primary/25 dark:bg-primary-light/10',
                      weekend && !isToday && 'bg-gray-50/50 dark:bg-gray-800/50'
                    )}
                  >
                    {format(parseISO(d), dateHeaderFormat)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paddingTop > 0 ? <SpacerRow height={paddingTop} colSpan={days.length + 1} /> : null}
            {visibleRows.map(({ row, index }) =>
              row.kind === 'group' ? (
                <CompanyGroupHeader
                  key={row.key}
                  index={index}
                  measureRef={measureRef}
                  companyId={row.companyId}
                  companyName={row.companyName}
                  colSpan={days.length + 1}
                />
              ) : (
                <UserRow
                  key={row.key}
                  index={index}
                  measureRef={measureRef}
                  user={row.user}
                  days={days}
                  assignmentsByCompanyUserDate={assignmentsByCompanyUserDate}
                  assignmentsByUserDate={assignmentsByUserDate}
                  shiftById={shiftById}
                  todayDateKey={todayDateKey}
                  isWeekend={isWeekend}
                  cellMinHeight={cellMinHeight}
                  tdCellPadding={tdCellPadding}
                  dateColWidth={dateColWidth}
                  stickyColClass={stickyColClass}
                  rowHoverClass={rowHoverClass}
                  isMonthly={isMonthly}
                  onAssign={onAssign}
                  onEdit={onEdit}
                  companyNameById={companyNameByIdMap}
                  highlightAssignmentId={highlightAssignmentId}
                  canCreate={canCreate}
                />
              )
            )}
            {paddingBottom > 0 ? (
              <SpacerRow height={paddingBottom} colSpan={days.length + 1} />
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
});

ShiftPlanningGrid.displayName = 'ShiftPlanningGrid';

interface RowSharedProps {
  days: string[];
  assignmentsByCompanyUserDate: Map<string, ShiftCellSegment[]>;
  assignmentsByUserDate: Map<string, ShiftCellSegment[]>;
  shiftById: Map<string, Shift>;
  todayDateKey: string;
  isWeekend: (dateKey: string) => boolean;
  cellMinHeight: string;
  tdCellPadding: string;
  dateColWidth: string;
  stickyColClass: string;
  rowHoverClass: string;
  isMonthly: boolean;
  onAssign: (
    userId: string,
    date: string,
    companyId?: string,
    companyOptions?: PlanningGridCompanyTag[]
  ) => void;
  onEdit: (assignment: ShiftAssignment) => void;
  /** Set of company ids the row covers in flat mode (used for tooltip/badge). */
  companyNameById?: Map<string, string>;
  /** Assignment to visually highlight. */
  highlightAssignmentId?: string;
  /** Whether the viewer may add shifts (the "Add shift" buttons). */
  canCreate: boolean;
}

/** Lets the virtualizer measure a row's real height (rows grow with their shifts). */
interface MeasuredRowProps {
  index: number;
  measureRef?: (node: Element | null) => void;
}

type GridRow =
  | { kind: 'group'; key: string; companyId: string; companyName: string }
  | { kind: 'user'; key: string; user: PlanningGridUser };

/** Stands in for the rows scrolled out of view, keeping the scrollbar true to the full list. */
function SpacerRow({ height, colSpan }: { height: number; colSpan: number }): React.ReactElement {
  return (
    <tr aria-hidden>
      <td colSpan={colSpan} style={{ height, padding: 0, border: 0 }} />
    </tr>
  );
}

const UserRow = memo(function UserRow({
  user,
  days,
  assignmentsByCompanyUserDate,
  assignmentsByUserDate,
  shiftById,
  todayDateKey,
  isWeekend,
  cellMinHeight,
  tdCellPadding,
  dateColWidth,
  stickyColClass,
  rowHoverClass,
  isMonthly,
  onAssign,
  onEdit,
  companyNameById,
  highlightAssignmentId,
  canCreate,
  index,
  measureRef,
}: RowSharedProps & MeasuredRowProps & { user: PlanningGridUser }): React.ReactElement {
  const flatMode = !user.companyId;
  return (
    <tr ref={measureRef} data-index={index} className={rowHoverClass}>
      <td className={stickyColClass}>
        <div className="flex min-w-0 items-center gap-2">
          <Avatar name={user.name} size="sm" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
              {user.name}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              <CompanyPills
                companyId={user.companyId}
                companyName={user.companyName}
                companyTags={user.companyTags}
              />
              <RolePills
                roleName={user.roleName}
                roleNames={user.roleNames}
                roleSlug={user.roleSlug}
              />
            </div>
          </div>
        </div>
      </td>
      {days.map((dateKey) => {
        const isToday = dateKey === todayDateKey;
        const weekend = isWeekend(dateKey);
        const cellClass = cn(
          dateColWidth,
          cellMinHeight,
          tdCellPadding,
          'align-top border-r border-gray-100 dark:border-gray-800',
          isToday && 'bg-primary/5 ring-inset ring-1 ring-primary/25 dark:bg-primary-light/10',
          weekend && !isToday && 'bg-gray-50/50 dark:bg-gray-800/50'
        );
        const cellSegments = flatMode
          ? (assignmentsByUserDate.get(`${user._id}:${dateKey}`) ?? [])
          : (assignmentsByCompanyUserDate.get(
              `${user.companyId ?? '__none__'}:${user._id}:${dateKey}`
            ) ?? []);
        const isPastDate = dateKey < todayDateKey;
        if (cellSegments.length > 0) {
          return (
            <td key={dateKey} className={cellClass}>
              <div className="flex flex-col gap-1">
                {cellSegments.map((segment) => {
                  const a = segment.assignment;
                  const shift = getAssignmentShift(a, shiftById);
                  const assignmentCompanyId =
                    (a as { companyId?: string }).companyId ?? user.companyId;
                  const cName =
                    assignmentCompanyId === user.companyId
                      ? user.companyName
                      : assignmentCompanyId
                        ? companyNameById?.get(assignmentCompanyId)
                        : undefined;
                  // Dot only when useful: flat mode (no row company) renders it,
                  // grouped rows already convey company via the section header.
                  const showCompanyDot = flatMode;
                  const isHighlighted = a._id === highlightAssignmentId;
                  return (
                    <div
                      key={`${a._id}:${segment.continuesFromPreviousDay ? 'next' : 'start'}`}
                      // Scroll anchor for deep-linked highlight; only the start segment gets the
                      // id so a multi-day shift doesn't produce duplicate DOM ids.
                      id={
                        segment.continuesFromPreviousDay ? undefined : `shift-assignment-${a._id}`
                      }
                      className={cn(
                        isHighlighted &&
                          'rounded-md ring-2 ring-primary ring-offset-1 dark:ring-offset-gray-900'
                      )}
                    >
                      <ShiftBlock
                        assignment={a}
                        shift={shift}
                        onClick={() => onEdit(a)}
                        variant={isMonthly ? 'monthly' : 'default'}
                        companyId={showCompanyDot ? assignmentCompanyId : undefined}
                        companyName={cName}
                        labelOverride={segment.continuesFromPreviousDay ? 'Continues' : undefined}
                        timeRangeOverride={
                          segment.continuesFromPreviousDay && shift?.endTime
                            ? `until ${shift.endTime}`
                            : undefined
                        }
                      />
                    </div>
                  );
                })}
                {!isPastDate && canCreate && (
                  <button
                    type="button"
                    onClick={() =>
                      onAssign(user._id, dateKey, user.companyId, user.companyTags)
                    }
                    className="group flex w-full items-center justify-center gap-1 rounded border border-dashed border-gray-200 px-2 py-1 text-[11px] text-gray-400 transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 dark:border-gray-700 dark:text-gray-500 dark:hover:border-primary"
                    aria-label="Add another shift for this day"
                  >
                    <Plus className="h-3 w-3" aria-hidden />
                    <span>Add shift</span>
                  </button>
                )}
              </div>
            </td>
          );
        }
        return (
          <td key={dateKey} className={cellClass}>
            {isPastDate ? (
              <PastDateCell variant={isMonthly ? 'monthly' : 'default'} />
            ) : (
              <EmptyCell
                onAssign={() =>
                  onAssign(user._id, dateKey, user.companyId, user.companyTags)
                }
                variant={isMonthly ? 'monthly' : 'default'}
                canCreate={canCreate}
              />
            )}
          </td>
        );
      })}
    </tr>
  );
});

const CompanyGroupHeader = memo(function CompanyGroupHeader({
  companyId,
  companyName,
  colSpan,
  index,
  measureRef,
}: MeasuredRowProps & {
  companyId: string;
  companyName: string;
  colSpan: number;
}): React.ReactElement {
  const color = getCompanyColor(companyId);
  return (
    <tr ref={measureRef} data-index={index} className="bg-gray-50 dark:bg-gray-900/40">
      <td
        colSpan={colSpan}
        className={cn('py-1.5 text-[11px] font-semibold uppercase tracking-wider', color.text)}
      >
        {/* The cell spans the whole table, so it can't stick itself; the
            label inside it does, staying in view as the grid scrolls across. */}
        <span className="sticky left-0 inline-flex items-center gap-1.5 px-3">
          <span className={cn('h-1.5 w-1.5 rounded-full', color.dotBg)} />
          {companyName} staff
        </span>
      </td>
    </tr>
  );
});
